"use client";

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { createProductChat, createCatalogProductChat } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import { Loader2, Star } from '@/components/icons';
import { Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import EmojiPicker from './EmojiPicker';

interface ProductChatFormProps {
  productId?: string;
  productCatalogId?: string;
  session: Session | null;
  storeId?: string;
  storeName?: string;
  lastPurchaseInfo?: { storeName?: string; purchaseDate: string } | null;
  isAuction?: boolean;
  onChatAdded: () => void;
}

const ProductChatForm: React.FC<ProductChatFormProps> = ({ productId, productCatalogId, session, storeId, storeName, lastPurchaseInfo, isAuction, onChatAdded }) => {
  const navigate = useNavigate();
  const [chatMessage, setChatMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!session?.user?.id) {
      toast({
        title: "Error",
        description: "Debes iniciar sesión para chatear.",
        variant: "destructive",
      });
      return;
    }
    if (!chatMessage.trim()) {
      toast({
        title: "Error",
        description: "El mensaje no puede estar vacío.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // For auction products, always use createProductChat (product_chats table)
      // The productCatalogId is just passed for reference but we don't use it for auctions
      if (productId) {
        // Only include rating for non-auction products (store products)
        await createProductChat(productId, session.user.id, chatMessage, !isAuction ? rating : null);
      } else {
        throw new Error('No productId provided for chat');
      }
      setChatMessage('');
      setRating(null);
      onChatAdded();
      // No toast aquí para mantener la experiencia de chat fluida
    } catch (error) {
      console.error("Error creating product chat message:", error);
      toast({
        title: "Error al enviar mensaje",
        description: "No se pudo enviar el mensaje. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    !session ? null : !isAuction && !lastPurchaseInfo ? (
      // For store products (non-auction), only verified buyers can comment
      <div className="space-y-2">
        <div className="p-3 bg-secondary/20 rounded-lg border border-primary/30">
          <p className="text-sm text-muted-foreground mb-3">
            Solo los usuarios que compraron este producto pueden comentar y dejar una calificación.
          </p>
        </div>
      </div>
    ) : (
      <form onSubmit={handleSubmit} className="space-y-0">
        {lastPurchaseInfo && (
          <div className="p-3 bg-secondary/20 rounded-lg border border-primary/30 mb-2">
            <p className="text-xs text-muted-foreground">
              Compraste este producto en tienda <span className="font-semibold text-foreground">{lastPurchaseInfo.storeName || 'desconocida'}</span> el <span className="font-semibold text-foreground">{lastPurchaseInfo.purchaseDate}</span>
            </p>
          </div>
        )}
        
        {/* Star Rating Selector - only for store products, not auctions */}
        {!isAuction && (
          <div className="flex flex-col gap-3 mb-5">
            <span className="text-sm text-muted-foreground">Calificación:</span>
            <div className="flex items-center gap-4">
              <div className="flex gap-2 touch-none select-none justify-center">
                {[1, 2, 3, 4, 5].map((starIndex) => (
                  <div key={starIndex} className="relative w-12 h-12 flex-shrink-0">
                    {/* Left half (0.5) button - covers left 50% */}
                    <button
                      type="button"
                      onClick={() => setRating(rating === starIndex - 0.5 ? null : starIndex - 0.5)}
                      onMouseEnter={() => setHoverRating(starIndex - 0.5)}
                      onMouseLeave={() => setHoverRating(null)}
                      className="absolute left-0 top-0 w-6 h-12 focus:outline-none z-10 cursor-pointer"
                      style={{ pointerEvents: 'auto' }}
                      aria-label={`Calificar con ${starIndex - 0.5} estrellas`}
                    />
                    {/* Right half (1) button - covers right 50% */}
                    <button
                      type="button"
                      onClick={() => setRating(rating === starIndex ? null : starIndex)}
                      onMouseEnter={() => setHoverRating(starIndex)}
                      onMouseLeave={() => setHoverRating(null)}
                      className="absolute right-0 top-0 w-6 h-12 focus:outline-none z-10 cursor-pointer"
                      style={{ pointerEvents: 'auto' }}
                      aria-label={`Calificar con ${starIndex} estrellas`}
                    />
                    {/* Star display */}
                    <div className="relative h-12 w-12 flex-shrink-0 cursor-pointer z-0" style={{ pointerEvents: 'none' }}>
                      {/* Background empty star */}
                      <Star className="h-12 w-12 text-muted-foreground absolute inset-0" />
                      {/* Filled overlay */}
                      {(hoverRating !== null ? hoverRating : rating) && (hoverRating !== null ? hoverRating : rating) >= starIndex - 0.5 && (
                        <div
                          className="absolute inset-0"
                          style={{
                            opacity: (hoverRating !== null ? hoverRating : rating) >= starIndex ? 1 : 0.4,
                          }}
                        >
                          <Star className="h-12 w-12 fill-yellow-400 text-yellow-400" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {(hoverRating !== null || rating) && (
                <span className="ml-4 text-2xl font-bold text-yellow-400">
                  {hoverRating !== null ? hoverRating : rating}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Input area - single line with buttons */}
        <div className="grid grid-cols-[1fr_auto_auto] items-end gap-2">
          {/* Text input */}
          <Textarea
            placeholder="Escribí algo..."
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading || !session}
            rows={1}
            aria-label="Escribe tu mensaje de chat"
            tabIndex={0}
            className="min-h-[40px] resize-none flex items-center py-2.5 !leading-tight"
          />

          {/* Emoji picker */}
          <EmojiPicker
            onEmojiSelect={(emoji) => setChatMessage(chatMessage + emoji)}
            disabled={loading || !session}
          />

          {/* Send button */}
          <Button
            type="submit"
            disabled={loading || !session || !chatMessage.trim()}
            size="icon"
            className="h-10 w-10 shrink-0"
            aria-label="Enviar mensaje de chat"
            tabIndex={0}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    )
  );
};

export default ProductChatForm;